import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/api';
import { formatCurrency, formatDate } from '../lib/format';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TemplateSelector } from '../components/TemplateSelector';
import { toast } from 'sonner';
import { Plus, Download, FileText, Trash2, Mail } from 'lucide-react';
import { SendDocumentModal } from '../components/SendDocumentModal';

const SENDABLE_STATUSES = new Set(['issued', 'partially_applied', 'fully_applied']);

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  issued: 'bg-blue-100 text-blue-700',
  partially_applied: 'bg-amber-100 text-amber-700',
  fully_applied: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

function todayIso() {
  return new Date().toISOString().split('T')[0];
}

function roundMoney(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return 0;
  return Math.round(x * 100) / 100;
}

/** Account tax rates are percentage points (e.g. 5 = 5%); credit note API uses decimal (0.05). */
function accountDefaultTaxPct(account) {
  if (!account) return 0;
  let pct = 0;
  if (account.gst_enabled) pct += parseFloat(account.gst_rate) || 0;
  if (account.pst_enabled) pct += parseFloat(account.pst_rate) || 0;
  return roundMoney(pct);
}

function templateTaxPct(template, account) {
  if (!account) return 0;
  let pct = 0;
  if (account.gst_enabled && template.taxable_gst !== false) {
    pct += parseFloat(account.gst_rate) || 0;
  }
  if (account.pst_enabled && template.taxable_pst) {
    pct += parseFloat(account.pst_rate) || 0;
  }
  return roundMoney(pct);
}

function emptyLineItem(account) {
  return {
    description: '',
    quantity: '1',
    unit_price: '',
    tax_rate_pct: String(accountDefaultTaxPct(account)),
  };
}

function calcLineTotal(li) {
  const qty = parseFloat(li.quantity) || 0;
  const price = parseFloat(li.unit_price) || 0;
  const taxPct = parseFloat(li.tax_rate_pct) || 0;
  const subtotal = qty * price;
  return roundMoney(subtotal + subtotal * (taxPct / 100));
}

function initialForm(account) {
  return {
    customer_id: '',
    issue_date: todayIso(),
    reason: '',
    notes: '',
    line_items: [emptyLineItem(account)],
  };
}

export default function CreditNotesPage() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [form, setForm] = useState(initialForm(null));
  const [saving, setSaving] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/credit-notes'),
      api.get('/clients'),
      api.get('/account'),
    ])
      .then(([cn, cl, acct]) => {
        setCreditNotes(Array.isArray(cn.data) ? cn.data : []);
        setClients(Array.isArray(cl.data) ? cl.data : []);
        setAccount(acct.data);
      })
      .catch(() => toast.error('Failed to load credit notes'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (showCreate) {
      setForm(initialForm(account));
    }
  }, [showCreate, account]);

  const formTotal = useMemo(
    () => form.line_items.reduce((sum, li) => sum + calcLineTotal(li), 0),
    [form.line_items]
  );

  const updateLineItem = (idx, patch) => {
    setForm((prev) => {
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], ...patch };
      return { ...prev, line_items: items };
    });
  };

  const removeLineItem = (idx) => {
    setForm((prev) => {
      if (prev.line_items.length <= 1) return prev;
      return { ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) };
    });
  };

  const handleTemplatesSelected = (templates) => {
    const mapped = templates.map((t) => ({
      description: t.description || '',
      quantity: t.quantity || '1',
      unit_price: t.unit_price || '0',
      tax_rate_pct: String(templateTaxPct(t, account)),
    }));
    setForm((prev) => ({
      ...prev,
      line_items: [...prev.line_items.filter((li) => li.description || li.unit_price), ...mapped],
    }));
    setShowTemplateSelector(false);
  };

  const handleCreate = async () => {
    if (!form.customer_id) {
      toast.error('Select a customer');
      return;
    }
    const lineItems = form.line_items
      .filter((li) => li.description && li.unit_price !== '')
      .map((li, idx) => ({
        description: li.description,
        quantity: parseFloat(li.quantity) || 1,
        unit_price: parseFloat(li.unit_price) || 0,
        tax_rate: roundMoney((parseFloat(li.tax_rate_pct) || 0) / 100),
        sort_order: idx,
      }));
    if (lineItems.length === 0) {
      toast.error('Add at least one line item');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        reason: form.reason,
        notes: form.notes,
        line_items: lineItems,
        issue_immediately: true,
      };
      if (form.issue_date) {
        payload.issue_date = new Date(`${form.issue_date}T12:00:00`).toISOString();
      }
      await api.post('/credit-notes', payload);
      toast.success('Credit note created');
      setShowCreate(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create credit note');
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (cn) => {
    try {
      const res = await api.get(`/credit-notes/${cn.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `CreditNote_${cn.credit_note_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Credit Notes</h1>
          <p className="text-sm text-slate-500">Customer credit memos to reduce amounts owed</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowCreate(true)} data-testid="create-credit-note">
          <Plus className="w-4 h-4 mr-2" /> New Credit Note
        </Button>
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <p className="p-8 text-center text-slate-500">Loading…</p>
          ) : creditNotes.length === 0 ? (
            <p className="p-8 text-center text-slate-500">No credit notes yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {creditNotes.map((cn) => (
                  <TableRow key={cn.id}>
                    <TableCell className="font-medium">{cn.credit_note_number}</TableCell>
                    <TableCell>{cn.customer_name}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${STATUS_COLORS[cn.status] || ''}`}>{cn.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(cn.issue_date)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(cn.total_amount)}</TableCell>
                    <TableCell className="text-right font-medium text-amber-600">{formatCurrency(cn.amount_remaining)}</TableCell>
                    <TableCell className="text-right">
                      {SENDABLE_STATUSES.has(cn.status) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSendTarget(cn)}
                          title="Send to customer"
                          data-testid={`send-credit-note-${cn.id}`}
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(cn)} title="Download PDF">
                        <Download className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle><FileText className="w-5 h-5 inline mr-2" />New Credit Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="cn-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cn-issue-date">Issue Date</Label>
                <Input
                  id="cn-issue-date"
                  type="date"
                  value={form.issue_date}
                  onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                  data-testid="cn-issue-date"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. Overpayment, pricing adjustment"
              />
            </div>

            <div className="flex items-center justify-between gap-2">
              <Label className="text-base font-medium">Line Items</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowTemplateSelector(true)}
                  data-testid="cn-add-from-templates"
                >
                  <Plus className="w-3 h-3 mr-1" /> From Templates
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setForm({ ...form, line_items: [...form.line_items, emptyLineItem(account)] })}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Line
                </Button>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="hidden sm:grid sm:grid-cols-[minmax(0,2fr)_72px_96px_72px_96px_40px] gap-2 px-3 py-2 bg-slate-50 text-xs font-medium text-slate-600">
                <span>Description</span>
                <span className="text-right">Qty</span>
                <span className="text-right">Unit Price</span>
                <span className="text-right">Tax %</span>
                <span className="text-right">Line Total</span>
                <span />
              </div>
              <div className="divide-y divide-slate-100">
                {form.line_items.map((li, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-1 sm:grid-cols-[minmax(0,2fr)_72px_96px_72px_96px_40px] gap-2 p-3 items-center"
                  >
                    <div className="space-y-1 sm:space-y-0">
                      <Label className="sm:hidden text-xs text-slate-500">Description</Label>
                      <Input
                        placeholder="Description"
                        value={li.description}
                        onChange={(e) => updateLineItem(idx, { description: e.target.value })}
                        data-testid={`cn-line-desc-${idx}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="sm:hidden text-xs text-slate-500">Qty</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="1"
                        className="text-right"
                        value={li.quantity}
                        onChange={(e) => updateLineItem(idx, { quantity: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="sm:hidden text-xs text-slate-500">Unit Price</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="text-right"
                        value={li.unit_price}
                        onChange={(e) => updateLineItem(idx, { unit_price: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="sm:hidden text-xs text-slate-500">Tax %</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={String(accountDefaultTaxPct(account))}
                        className="text-right"
                        value={li.tax_rate_pct}
                        onChange={(e) => updateLineItem(idx, { tax_rate_pct: e.target.value })}
                        data-testid={`cn-line-tax-${idx}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="sm:hidden text-xs text-slate-500">Line Total</Label>
                      <div className="h-10 flex items-center justify-end text-sm font-medium text-slate-900 px-1">
                        {formatCurrency(calcLineTotal(li))}
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-slate-400 hover:text-rose-600"
                        onClick={() => removeLineItem(idx)}
                        disabled={form.line_items.length <= 1}
                        title="Remove line"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end text-sm">
              <span className="text-slate-500 mr-2">Credit total:</span>
              <span className="font-semibold text-slate-900">{formatCurrency(formTotal)}</span>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating…' : 'Create & Issue'}
            </Button>
          </DialogFooter>
        </DialogContent>
        <TemplateSelector
          open={showTemplateSelector}
          onClose={() => setShowTemplateSelector(false)}
          documentDate={form.issue_date}
          onSelectTemplates={handleTemplatesSelected}
        />
      </Dialog>

      <SendDocumentModal
        open={!!sendTarget}
        onClose={() => setSendTarget(null)}
        documentType="credit-note"
        documentId={sendTarget?.id}
        documentNumber={sendTarget?.credit_note_number}
      />
    </div>
  );
}
